import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FieldHelpIcon from '../../../components/common/FieldHelpIcon';
import CareLinkFieldLabel from './CareLinkFieldLabel';
import CareLinkLineListField from './CareLinkLineListField';

const BLOOD_TYPES = [
  'A_POSITIVE', 'A_NEGATIVE', 'B_POSITIVE', 'B_NEGATIVE',
  'AB_POSITIVE', 'AB_NEGATIVE', 'O_POSITIVE', 'O_NEGATIVE', 'UNKNOWN',
];

const COMMUNICATION_TYPES = [
  'VERBAL', 'NON_VERBAL', 'LIMITED_VERBAL', 'SIGN_LANGUAGE',
  'PICTOGRAMS', 'WRITTEN', 'OTHER',
];

const CONTACT_ROLES = [
  'MOTHER', 'FATHER', 'TUTOR', 'LEGAL_GUARDIAN', 'PARTNER', 'SON', 'DAUGHTER',
  'BROTHER', 'SISTER', 'CAREGIVER', 'FAMILY_MEMBER', 'FRIEND', 'OTHER',
];

const CARE_CENTER_TYPES = [
  'RESIDENTIAL_CENTER', 'DAY_CENTER', 'HOSPITAL', 'HEALTH_CENTER',
  'SCHOOL', 'SPECIAL_EDUCATION_CENTER', 'ASSISTED_LIVING', 'OTHER',
];

const emptyContact = () => ({
  name: '',
  role: 'FAMILY_MEMBER',
  phone: '',
  secondaryPhone: '',
  primaryContact: false,
  emergencyContact: true,
  enabled: true,
});

const emptyMedication = () => ({
  name: '',
  activeIngredient: '',
  dosage: '',
  administrationRoute: '',
  schedule: '',
  instructions: '',
  active: true,
});

const emptyDoctor = () => ({
  name: '',
  medicalCenter: '',
  specialty: '',
  phone: '',
});

const emptyCareCenter = () => ({
  name: '',
  type: '',
  address: '',
  city: '',
  postalCode: '',
  country: '',
  phone: '',
});

const FieldError = ({ message }) => (
  message ? <span className="field-error-message">{message}</span> : null
);

const CareLinkFormContent = ({ formData, onChange, isEditing, fieldErrors = {} }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('identity');

  useEffect(() => {
    setActiveTab('identity');
  }, [isEditing, formData?.id]);

  const updateField = (field, value) => {
    onChange({
      ...formData,
      [field]: value,
    });
  };

  const updateNested = (section, field, value) => {
    onChange({
      ...formData,
      [section]: {
        ...(formData?.[section] || {}),
        [field]: value,
      },
    });
  };

  const updateListItem = (listKey, index, field, value) => {
    const list = Array.isArray(formData?.[listKey]) ? [...formData[listKey]] : [];
    list[index] = {
      ...list[index],
      [field]: value,
    };
    onChange({
      ...formData,
      [listKey]: list,
    });
  };

  const addListItem = (listKey, factory) => {
    const list = Array.isArray(formData?.[listKey]) ? [...formData[listKey]] : [];
    onChange({
      ...formData,
      [listKey]: [...list, factory()],
    });
  };

  const removeListItem = (listKey, index) => {
    const list = Array.isArray(formData?.[listKey]) ? [...formData[listKey]] : [];
    list.splice(index, 1);
    onChange({
      ...formData,
      [listKey]: list,
    });
  };

  const doctor = formData?.doctor || emptyDoctor();
  const careCenter = formData?.careCenter || emptyCareCenter();
  const contacts = Array.isArray(formData?.contacts) ? formData.contacts : [];
  const medications = Array.isArray(formData?.medications) ? formData.medications : [];

  const tabs = [
    { id: 'identity', label: t('carelink.tabs.identity') },
    { id: 'medical', label: t('carelink.tabs.medical') },
    { id: 'assistance', label: t('carelink.tabs.assistance') },
    { id: 'contacts', label: t('carelink.tabs.contacts') },
    { id: 'medications', label: t('carelink.tabs.medications') },
    { id: 'healthcare', label: t('carelink.tabs.healthcare') },
  ];

  return (
    <div className="tabs carelink-manage-form">
      <ul className="nav nav-tabs">
        {tabs.map((tab) => (
          <li key={tab.id} className={activeTab === tab.id ? 'active' : ''}>
            <a
              href={`#carelink-${tab.id}`}
              onClick={(event) => {
                event.preventDefault();
                setActiveTab(tab.id);
              }}
              style={{ cursor: 'pointer' }}
            >
              {tab.label}
            </a>
          </li>
        ))}
      </ul>

      <div className="tab-content" style={{ paddingTop: '15px' }}>
        {activeTab === 'identity' && (
          <div className="tab-pane active">
            <div className="form-row-2cols">
              <div className="form-group">
                <label htmlFor="carelink-firstName">
                  {t('carelink.firstName')} <span className="required-field">*</span>
                </label>
                <input
                  id="carelink-firstName"
                  type="text"
                  className={`form-control ${fieldErrors.firstName ? 'has-error' : ''}`}
                  value={formData?.firstName || ''}
                  onChange={(e) => updateField('firstName', e.target.value)}
                />
                <FieldError message={fieldErrors.firstName} />
              </div>
              <div className="form-group">
                <label htmlFor="carelink-lastName">
                  {t('carelink.lastName')} <span className="required-field">*</span>
                </label>
                <input
                  id="carelink-lastName"
                  type="text"
                  className={`form-control ${fieldErrors.lastName ? 'has-error' : ''}`}
                  value={formData?.lastName || ''}
                  onChange={(e) => updateField('lastName', e.target.value)}
                />
                <FieldError message={fieldErrors.lastName} />
              </div>
              <div className="form-group">
                <label htmlFor="carelink-secondLastName">{t('carelink.secondLastName')}</label>
                <input
                  id="carelink-secondLastName"
                  type="text"
                  className="form-control"
                  value={formData?.secondLastName || ''}
                  onChange={(e) => updateField('secondLastName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="carelink-dateOfBirth">{t('carelink.dateOfBirth')}</label>
                <input
                  id="carelink-dateOfBirth"
                  type="date"
                  className="form-control"
                  value={formData?.dateOfBirth || ''}
                  onChange={(e) => updateField('dateOfBirth', e.target.value)}
                />
              </div>
              <div className="form-group">
                <CareLinkFieldLabel
                  htmlFor="carelink-preferredLanguage"
                  label={t('carelink.preferredLanguage')}
                  help={t('carelink.helps.preferredLanguage')}
                />
                <input
                  id="carelink-preferredLanguage"
                  type="text"
                  className="form-control"
                  value={formData?.preferredLanguage || ''}
                  onChange={(e) => updateField('preferredLanguage', e.target.value)}
                  placeholder={t('carelink.preferredLanguagePlaceholder')}
                />
              </div>
              <div className="form-group">
                <div className="carelink-consent-row">
                  <input
                    id="carelink-identificationNumberPublic"
                    type="checkbox"
                    checked={Boolean(formData?.identificationNumberPublic)}
                    onChange={(e) => updateField('identificationNumberPublic', e.target.checked)}
                  />
                  <label htmlFor="carelink-identificationNumberPublic">
                    {t('carelink.identificationNumberPublicConsent')}
                  </label>
                </div>
                <label htmlFor="carelink-identificationNumber">{t('carelink.identificationNumber')}</label>
                <input
                  id="carelink-identificationNumber"
                  type="text"
                  className="form-control"
                  value={formData?.identificationNumber || ''}
                  onChange={(e) => updateField('identificationNumber', e.target.value)}
                  disabled={!formData?.identificationNumberPublic}
                  placeholder={formData?.identificationNumberPublic ? '' : t('carelink.publicFieldDisabledHint')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="carelink-socialSecurityNumber">{t('carelink.socialSecurityNumber')}</label>
                <input
                  id="carelink-socialSecurityNumber"
                  type="text"
                  className="form-control"
                  value={formData?.socialSecurityNumber || ''}
                  onChange={(e) => updateField('socialSecurityNumber', e.target.value)}
                />
              </div>
              <div className="form-group">
                <div className="carelink-consent-row">
                  <input
                    id="carelink-healthCardNumberPublic"
                    type="checkbox"
                    checked={Boolean(formData?.healthCardNumberPublic)}
                    onChange={(e) => updateField('healthCardNumberPublic', e.target.checked)}
                  />
                  <label htmlFor="carelink-healthCardNumberPublic">
                    {t('carelink.healthCardNumberPublicConsent')}
                  </label>
                </div>
                <label htmlFor="carelink-healthCardNumber">{t('carelink.healthCardNumber')}</label>
                <input
                  id="carelink-healthCardNumber"
                  type="text"
                  className="form-control"
                  value={formData?.healthCardNumber || ''}
                  onChange={(e) => updateField('healthCardNumber', e.target.value)}
                  disabled={!formData?.healthCardNumberPublic}
                  placeholder={formData?.healthCardNumberPublic ? '' : t('carelink.publicFieldDisabledHint')}
                />
              </div>
              {isEditing && (
                <div className="form-group">
                  <label htmlFor="carelink-publicToken">{t('carelink.publicToken')}</label>
                  <input
                    id="carelink-publicToken"
                    type="text"
                    className="form-control"
                    value={formData?.publicToken || ''}
                    readOnly
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'medical' && (
          <div className="tab-pane active">
            <div className="form-row-2cols">
              <div className="form-group">
                <CareLinkFieldLabel
                  htmlFor="carelink-bloodTypeEnum"
                  label={t('carelink.bloodType')}
                  help={t('carelink.helps.bloodType')}
                />
                <select
                  id="carelink-bloodTypeEnum"
                  className="form-control"
                  value={formData?.bloodTypeEnum || ''}
                  onChange={(e) => updateField('bloodTypeEnum', e.target.value || null)}
                >
                  <option value="">{t('common.select')}</option>
                  {BLOOD_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`carelink.bloodTypes.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '28px' }}>
                <input
                  id="carelink-hasDisability"
                  type="checkbox"
                  checked={Boolean(formData?.hasDisability)}
                  onChange={(e) => updateField('hasDisability', e.target.checked)}
                />
                <label htmlFor="carelink-hasDisability" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {t('carelink.hasDisability')}
                  <FieldHelpIcon help={t('carelink.helps.hasDisability')} />
                </label>
              </div>
              <div className="form-group">
                <CareLinkFieldLabel
                  htmlFor="carelink-disabilityType"
                  label={t('carelink.disabilityType')}
                  help={t('carelink.helps.disabilityType')}
                />
                <input
                  id="carelink-disabilityType"
                  type="text"
                  className="form-control"
                  value={formData?.disabilityType || ''}
                  onChange={(e) => updateField('disabilityType', e.target.value)}
                  disabled={!formData?.hasDisability}
                />
              </div>
            </div>
            <CareLinkLineListField
              id="carelink-criticalInformation"
              label={t('carelink.criticalInformation')}
              help={t('carelink.helps.criticalInformation')}
              value={formData?.criticalInformation || ''}
              onChange={(value) => updateField('criticalInformation', value)}
              placeholder={t('carelink.lineList.placeholders.criticalInformation')}
            />
            <CareLinkLineListField
              id="carelink-medicalConditions"
              label={t('carelink.medicalConditions')}
              help={t('carelink.helps.medicalConditions')}
              value={formData?.medicalConditions || ''}
              onChange={(value) => updateField('medicalConditions', value)}
              placeholder={t('carelink.lineList.placeholders.medicalConditions')}
            />
            <div className="form-row-2cols">
              <CareLinkLineListField
                id="carelink-foodAllergies"
                label={t('carelink.foodAllergies')}
                help={t('carelink.helps.foodAllergies')}
                value={formData?.foodAllergies || ''}
                onChange={(value) => updateField('foodAllergies', value)}
                placeholder={t('carelink.lineList.placeholders.foodAllergies')}
              />
              <CareLinkLineListField
                id="carelink-medicationAllergies"
                label={t('carelink.medicationAllergies')}
                help={t('carelink.helps.medicationAllergies')}
                value={formData?.medicationAllergies || ''}
                onChange={(value) => updateField('medicationAllergies', value)}
                placeholder={t('carelink.lineList.placeholders.medicationAllergies')}
              />
            </div>
            <CareLinkLineListField
              id="carelink-relevantMedicalInformation"
              label={t('carelink.relevantMedicalInformation')}
              help={t('carelink.helps.relevantMedicalInformation')}
              value={formData?.relevantMedicalInformation || ''}
              onChange={(value) => updateField('relevantMedicalInformation', value)}
              placeholder={t('carelink.lineList.placeholders.relevantMedicalInformation')}
            />
          </div>
        )}

        {activeTab === 'assistance' && (
          <div className="tab-pane active">
            <div className="form-row-2cols">
              <div className="form-group">
                <CareLinkFieldLabel
                  htmlFor="carelink-communicationTypeEnum"
                  label={t('carelink.communicationType')}
                  help={t('carelink.helps.communicationType')}
                />
                <select
                  id="carelink-communicationTypeEnum"
                  className="form-control"
                  value={formData?.communicationTypeEnum || ''}
                  onChange={(e) => updateField('communicationTypeEnum', e.target.value || null)}
                >
                  <option value="">{t('common.select')}</option>
                  {COMMUNICATION_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`carelink.communicationTypes.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '28px' }}>
                <input
                  id="carelink-requiresSupervision"
                  type="checkbox"
                  checked={Boolean(formData?.requiresSupervision)}
                  onChange={(e) => updateField('requiresSupervision', e.target.checked)}
                />
                <label htmlFor="carelink-requiresSupervision" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {t('carelink.requiresSupervision')}
                  <FieldHelpIcon help={t('carelink.helps.requiresSupervision')} />
                </label>
              </div>
            </div>
            <CareLinkLineListField
              id="carelink-communicationInstructions"
              label={t('carelink.communicationInstructions')}
              help={t('carelink.helps.communicationInstructions')}
              value={formData?.communicationInstructions || ''}
              onChange={(value) => updateField('communicationInstructions', value)}
              placeholder={t('carelink.lineList.placeholders.communicationInstructions')}
            />
            <CareLinkLineListField
              id="carelink-assistanceInstructions"
              label={t('carelink.assistanceInstructions')}
              help={t('carelink.helps.assistanceInstructions')}
              value={formData?.assistanceInstructions || ''}
              onChange={(value) => updateField('assistanceInstructions', value)}
              placeholder={t('carelink.lineList.placeholders.assistanceInstructions')}
            />
            <CareLinkLineListField
              id="carelink-supervisionInstructions"
              label={t('carelink.supervisionInstructions')}
              help={t('carelink.helps.supervisionInstructions')}
              value={formData?.supervisionInstructions || ''}
              onChange={(value) => updateField('supervisionInstructions', value)}
              placeholder={t('carelink.lineList.placeholders.supervisionInstructions')}
              disabled={!formData?.requiresSupervision}
            />
            <CareLinkLineListField
              id="carelink-emergencyInstructions"
              label={t('carelink.emergencyInstructions')}
              help={t('carelink.helps.emergencyInstructions')}
              value={formData?.emergencyInstructions || ''}
              onChange={(value) => updateField('emergencyInstructions', value)}
              placeholder={t('carelink.lineList.placeholders.emergencyInstructions')}
            />
            <div className="form-row-2cols">
              <CareLinkLineListField
                id="carelink-thingsToAvoid"
                label={t('carelink.thingsToAvoid')}
                help={t('carelink.helps.thingsToAvoid')}
                value={formData?.thingsToAvoid || ''}
                onChange={(value) => updateField('thingsToAvoid', value)}
                placeholder={t('carelink.lineList.placeholders.thingsToAvoid')}
              />
              <CareLinkLineListField
                id="carelink-preferences"
                label={t('carelink.preferences')}
                help={t('carelink.helps.preferences')}
                value={formData?.preferences || ''}
                onChange={(value) => updateField('preferences', value)}
                placeholder={t('carelink.lineList.placeholders.preferences')}
              />
            </div>
            <CareLinkLineListField
              id="carelink-importantInformation"
              label={t('carelink.importantInformation')}
              help={t('carelink.helps.importantInformation')}
              value={formData?.importantInformation || ''}
              onChange={(value) => updateField('importantInformation', value)}
              placeholder={t('carelink.lineList.placeholders.importantInformation')}
            />
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="tab-pane active">
            <FieldError message={fieldErrors.contacts} />
            {contacts.map((contact, index) => (
              <div key={`contact-${index}`} className="carelink-nested-block">
                <div className="carelink-nested-header">
                  <strong>{t('carelink.contact')} #{index + 1}</strong>
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => removeListItem('contacts', index)}
                  >
                    <i className="fa fa-trash"></i> {t('common.delete')}
                  </button>
                </div>
                <div className="form-row-2cols">
                  <div className="form-group">
                    <label>{t('carelink.contactName')} <span className="required-field">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={contact.name || ''}
                      onChange={(e) => updateListItem('contacts', index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('carelink.contactRole')} <span className="required-field">*</span></label>
                    <select
                      className="form-control"
                      value={contact.role || 'OTHER'}
                      onChange={(e) => updateListItem('contacts', index, 'role', e.target.value)}
                    >
                      {CONTACT_ROLES.map((value) => (
                        <option key={value} value={value}>
                          {t(`carelink.roles.${value}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('carelink.phone')} <span className="required-field">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={contact.phone || ''}
                      onChange={(e) => updateListItem('contacts', index, 'phone', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('carelink.secondaryPhone')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={contact.secondaryPhone || ''}
                      onChange={(e) => updateListItem('contacts', index, 'secondaryPhone', e.target.value)}
                    />
                  </div>
                </div>
                <div className="carelink-checkbox-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(contact.primaryContact)}
                      onChange={(e) => updateListItem('contacts', index, 'primaryContact', e.target.checked)}
                    />
                    {' '}{t('carelink.primaryContact')}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(contact.emergencyContact)}
                      onChange={(e) => updateListItem('contacts', index, 'emergencyContact', e.target.checked)}
                    />
                    {' '}{t('carelink.emergencyContact')}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={contact.enabled !== false}
                      onChange={(e) => updateListItem('contacts', index, 'enabled', e.target.checked)}
                    />
                    {' '}{t('carelink.enabled')}
                  </label>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => addListItem('contacts', emptyContact)}
            >
              <i className="fa fa-plus"></i> {t('carelink.addContact')}
            </button>
          </div>
        )}

        {activeTab === 'medications' && (
          <div className="tab-pane active">
            <FieldError message={fieldErrors.medications} />
            {medications.map((medication, index) => (
              <div key={`medication-${index}`} className="carelink-nested-block">
                <div className="carelink-nested-header">
                  <strong>{t('carelink.medicationItem')} #{index + 1}</strong>
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => removeListItem('medications', index)}
                  >
                    <i className="fa fa-trash"></i> {t('common.delete')}
                  </button>
                </div>
                <div className="form-row-2cols">
                  <div className="form-group">
                    <label>{t('carelink.medication.name')} <span className="required-field">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={medication.name || ''}
                      onChange={(e) => updateListItem('medications', index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('carelink.medication.activeIngredient')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={medication.activeIngredient || ''}
                      onChange={(e) => updateListItem('medications', index, 'activeIngredient', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('carelink.medication.dosage')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={medication.dosage || ''}
                      onChange={(e) => updateListItem('medications', index, 'dosage', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('carelink.medication.route')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={medication.administrationRoute || ''}
                      onChange={(e) => updateListItem('medications', index, 'administrationRoute', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('carelink.medication.schedule')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={medication.schedule || ''}
                      onChange={(e) => updateListItem('medications', index, 'schedule', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '28px' }}>
                    <input
                      type="checkbox"
                      checked={medication.active !== false}
                      onChange={(e) => updateListItem('medications', index, 'active', e.target.checked)}
                    />
                    <span>{t('carelink.medication.active')}</span>
                  </div>
                </div>
                <CareLinkLineListField
                  id={`carelink-medication-instructions-${index}`}
                  label={t('carelink.instructions')}
                  help={t('carelink.helps.medicationInstructions')}
                  value={medication.instructions || ''}
                  onChange={(value) => updateListItem('medications', index, 'instructions', value)}
                  placeholder={t('carelink.lineList.placeholders.medicationInstructions')}
                />
              </div>
            ))}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => addListItem('medications', emptyMedication)}
            >
              <i className="fa fa-plus"></i> {t('carelink.addMedication')}
            </button>
          </div>
        )}

        {activeTab === 'healthcare' && (
          <div className="tab-pane active">
            <h4 className="carelink-section-heading">{t('carelink.doctor')}</h4>
            <FieldError message={fieldErrors.doctor} />
            <div className="form-row-2cols">
              <div className="form-group">
                <label>{t('carelink.doctorName')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={doctor.name || ''}
                  onChange={(e) => updateNested('doctor', 'name', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.phone')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={doctor.phone || ''}
                  onChange={(e) => updateNested('doctor', 'phone', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.specialty')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={doctor.specialty || ''}
                  onChange={(e) => updateNested('doctor', 'specialty', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.medicalCenter')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={doctor.medicalCenter || ''}
                  onChange={(e) => updateNested('doctor', 'medicalCenter', e.target.value)}
                />
              </div>
            </div>

            <h4 className="carelink-section-heading">
              {t('carelink.careCenter')}
              <span className="carelink-optional-hint"> ({t('carelink.optional')})</span>
            </h4>
            <p className="carelink-section-hint">{t('carelink.careCenterOptionalHint')}</p>
            <FieldError message={fieldErrors.careCenter} />
            <div className="form-row-2cols">
              <div className="form-group">
                <label>{t('carelink.careCenterName')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={careCenter.name || ''}
                  onChange={(e) => updateNested('careCenter', 'name', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.careCenterType')}</label>
                <select
                  className="form-control"
                  value={careCenter.type || ''}
                  onChange={(e) => updateNested('careCenter', 'type', e.target.value || null)}
                >
                  <option value="">{t('common.select')}</option>
                  {CARE_CENTER_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`carelink.careCenterTypes.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('carelink.address')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={careCenter.address || ''}
                  onChange={(e) => updateNested('careCenter', 'address', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.city')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={careCenter.city || ''}
                  onChange={(e) => updateNested('careCenter', 'city', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.postalCode')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={careCenter.postalCode || ''}
                  onChange={(e) => updateNested('careCenter', 'postalCode', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.country')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={careCenter.country || ''}
                  onChange={(e) => updateNested('careCenter', 'country', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('carelink.phone')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={careCenter.phone || ''}
                  onChange={(e) => updateNested('careCenter', 'phone', e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareLinkFormContent;
export {
  emptyContact,
  emptyMedication,
  emptyDoctor,
  emptyCareCenter,
};
